import React, { cloneElement, useCallback, useContext, useEffect, useLayoutEffect, } from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import ReactResizeObserver from 'choerodon-ui/lib/_util/resizeObserver';
import TableContext from './TableContext';
import TableRow from './TableRow';
import { ColumnLock, DragColumnAlign, GroupType } from './enum';
import ExpandedRow from './ExpandedRow';
import { DataSetStatus } from '../data-set/enum';
import { instance } from './Table';
import { getHeader, isStickySupport } from './utils';
import TableRowGroup from './TableRowGroup';
import { $l } from '../locale-context';
import Button from '../button/Button';
import { Size } from '../core/enum';
import { ButtonColor, FuncType } from '../button/enum';
import { defaultAggregationRenderer } from './Column';
import VirtualVerticalContainer from './VirtualVerticalContainer';
import useComputed from '../use-computed';
import VirtualRowMetaData from './VirtualRowMetaData';
import { toTransformValue } from '../_util/transform';
import { useRenderClone } from './hooks';
function generateRowGroup(props) {
    const { tableStore, columnGroups, lock, children, statistics, key, rowGroupLevel } = props;
    const level = rowGroupLevel ? rowGroupLevel.count : 0;
    const node = (React.createElement(TableRowGroup, { key: key, columnGroups: columnGroups, lock: lock, level: level }, children));
    if (statistics) {
        const { rowMetaData } = statistics;
        if (rowMetaData) {
            const currentRowMetaData = new VirtualRowMetaData(tableStore, 'group', statistics.lastRowMetaData);
            currentRowMetaData.groupLevel = level;
            currentRowMetaData.node = node;
            rowMetaData.push(currentRowMetaData);
            statistics.lastRowMetaData = currentRowMetaData;
        }
    }
    return node;
}
function generateRow(props) {
    const { tableStore, record, parentExpanded, lock, columnGroups, groupPath, index, statistics, expandIconColumnIndex, children, headerGroup, draggableId, dragRowHeight, } = props;
    const { key } = record;
    const tableRowProps = {
        columnGroups,
        record,
        index: index.count,
        key,
        hidden: !parentExpanded,
        lock,
        expandIconColumnIndex,
        groupPath,
        children,
    };
    if (statistics) {
        const { rowMetaData } = statistics;
        tableRowProps.virtualIndex = statistics.count;
        statistics.count++;
        if (rowMetaData) {
            const currentRowMetaData = new VirtualRowMetaData(tableStore, 'row', statistics.lastRowMetaData, record);
            rowMetaData.push(currentRowMetaData);
            statistics.lastRowMetaData = currentRowMetaData;
            tableRowProps.metaData = currentRowMetaData;
        }
    }
    index.count++;
    if (headerGroup) {
        tableRowProps.headerGroupIndex = headerGroup.count;
        headerGroup.count++;
    }
    if (dragRowHeight && statistics && !statistics.dragTargetFound) {
        if (draggableId && String(key) === String(draggableId)) {
            statistics.dragTargetFound = true;
        }
        else {
            tableRowProps.style = {
                transform: toTransformValue({
                    translateY: pxToRem(-dragRowHeight),
                }),
            };
        }
    }
    return (React.createElement(TableRow, Object.assign({}, tableRowProps)));
}
function renderExpandedRows(rowProps) {
    const index = { count: 0 };
    const rows = [];
    const { record: parent } = rowProps;
    (parent.children || []).forEach((record) => generateRowAndChildRows(rows, {
        ...rowProps,
        record,
        index,
    }));
    return rows;
}
function generateDraggableRow(props) {
    const { tableStore, record, lock, index, rowDragRender, statistics, isTree, virtual, rowDraggable } = props;
    const children = isTree && !virtual && (React.createElement(ExpandedRow, Object.assign({}, props, { renderExpandedRows: renderExpandedRows })));
    const draggableIndex = rowDraggable && isTree && statistics ? statistics.count : index.count;
    const row = generateRow({ ...props, children });
    if (rowDraggable) {
        const { dragColumnAlign } = tableStore;
        if (!dragColumnAlign ||
            (dragColumnAlign === DragColumnAlign.right && lock !== ColumnLock.left) ||
            (dragColumnAlign === DragColumnAlign.left && lock !== ColumnLock.right)) {
            const { key } = record;
            return (React.createElement(Draggable, { draggableId: String(key), index: draggableIndex, key: key }, (provided, snapshot) => (cloneElement(row, { provided, snapshot, ...(rowDragRender && rowDragRender.draggableProps) }))));
        }
    }
    return row;
}
function generateRowAndChildRows(rows, props) {
    const { tableStore, record, isTree, virtual } = props;
    rows.push(generateDraggableRow(props));
    if (isTree && virtual && tableStore.isRowExpanded(record)) {
        (record.children || []).forEach(record => {
            generateRowAndChildRows(rows, {
                ...props,
                index: { count: 0 },
                record,
            });
        });
    }
    return rows;
}
function generateCachedRows(props, handleClearCache, statistics) {
    const { cachedData: records } = props.tableStore;
    if (records.length) {
        const index = { count: 0 };
        const rows = [
            generateRowGroup({
                ...props,
                key: '$$group-cached-rows',
                statistics,
                children: (React.createElement(React.Fragment, null,
                    React.createElement("span", null, $l('Table', 'cached_records')),
                    React.createElement(Button, { funcType: FuncType.link, color: ButtonColor.primary, icon: "delete", size: Size.small, onClick: handleClearCache }))),
            }),
        ];
        records.forEach(record => rows.push(generateRow({
            ...props,
            record,
            index,
            statistics,
            parentExpanded: true,
        })));
        return rows;
    }
    return [];
}
function generateNormalRows(rows, records, props, statistics, index = { count: 0 }) {
    records.forEach((record) => generateRowAndChildRows(rows, {
        ...props,
        record,
        index,
        statistics,
        parentExpanded: true,
    }));
    return rows;
}
function generateGroupRows(rows, groups, props, hasCached, statistics, groupPath = [], index = { count: 0 }, isParentLast) {
    const { columnGroups, lock, tableStore } = props;
    const { groups: tableGroups, dataSet, prefixCls } = tableStore;
    const { length } = groups;
    groups.forEach((group, i) => {
        const { subGroups, records, name, subHGroups, children } = group;
        const hasChildren = children && tableStore.isGroupExpanded(group);
        const isLastInTreeNode = i === length - 1 && isParentLast !== false;
        const isLast = isLastInTreeNode && !hasChildren;
        const path = [...groupPath, [group, isLast]];
        const tableGroup = tableGroups && tableGroups.find(g => g.name === name);
        if (tableGroup && tableGroup.type === GroupType.row) {
            const { columnProps } = tableGroup;
            const { renderer = defaultAggregationRenderer } = columnProps || {};
            const groupName = tableGroup.name;
            const header = getHeader({ ...columnProps, name: groupName, dataSet, group });
            rows.push(generateRowGroup({
                key: `$group-${path.map(([g]) => g.value).join('-')}`,
                columnGroups,
                lock,
                tableStore,
                statistics,
                children: (React.createElement(React.Fragment, null,
                    header,
                    header && React.createElement("span", { className: `${prefixCls}-row-group-divider` }),
                    renderer({ text: group.value, rowGroup: group, name: groupName, dataSet, record: group.totalRecords[0] }))),
                rowGroupLevel: { count: path.length - (hasCached ? 0 : 1) },
            }));
        }
        if (subHGroups) {
            const $index = { count: 0 };
            subHGroups.forEach((group) => {
                group.records.slice($index.count).forEach((record) => {
                    generateRowAndChildRows(rows, {
                        ...props,
                        record,
                        index,
                        statistics,
                        headerGroup: $index,
                        groupPath: path,
                        parentExpanded: true,
                    });
                });
            });
        }
        else {
            if (subGroups && subGroups.length) {
                generateGroupRows(rows, subGroups, props, hasCached, statistics, path, index, isLast);
            }
            records.forEach((record) => {
                generateRowAndChildRows(rows, {
                    ...props,
                    record,
                    index,
                    statistics,
                    groupPath: path,
                    parentExpanded: true,
                });
            });
        }
        if (hasChildren) {
            generateGroupRows(rows, children, props, hasCached, statistics, groupPath, undefined, isLastInTreeNode);
        }
    });
    return rows;
}
function generateRows(props, hasCached, statistics) {
    const { tableStore } = props;
    const { currentData, groupedData } = tableStore;
    const rows = [];
    if (hasCached && currentData.length) {
        const { columnGroups, lock } = props;
        rows.push(generateRowGroup({
            key: '$$group-rows',
            columnGroups,
            lock,
            tableStore,
            statistics,
            children: $l('Table', 'current_page_records'),
        }));
    }
    if (groupedData.length) {
        generateGroupRows(rows, groupedData, props, hasCached, statistics);
    }
    else if (currentData.length) {
        generateNormalRows(rows, currentData, props, statistics);
    }
    return rows;
}
function getEmptyRow(props) {
    const { tableStore, columnGroups, lock } = props;
    const { emptyText, width, prefixCls, dataSet } = tableStore;
    const styles = width ? {
        position: isStickySupport() ? 'sticky' : 'relative',
        left: pxToRem(width / 2),
    } : {
        transform: 'none',
        display: 'inline-block',
    };
    const tdStyle = width ? undefined : { textAlign: 'center' };
    return (React.createElement("tr", { className: `${prefixCls}-empty-row` },
        React.createElement("td", { colSpan: columnGroups.leafs.length, style: tdStyle },
            React.createElement("div", { style: styles }, !lock && dataSet.status === DataSetStatus.ready && emptyText))));
}
const VirtualRows = function VirtualRows(props) {
    const { lock, columnGroups, onClearCache, expandIconColumnIndex, tableStore, rowDragRender, isTree, rowDraggable, snapshot, dragRowHeight } = props;
    const draggableId = snapshot && snapshot.draggingFromThisWith;
    const [totalRows, statistics] = useComputed(() => {
        const $statistics = { count: 0 };
        if (!tableStore.isFixedRowHeight || (isTree && rowDraggable) || tableStore.hasRowGroups) {
            $statistics.rowMetaData = [];
        }
        const cachedRows = generateCachedRows({ tableStore, columnGroups, lock, isTree, rowDraggable, virtual: true }, onClearCache, $statistics);
        const rows = generateRows({
            tableStore,
            columnGroups,
            expandIconColumnIndex,
            lock,
            rowDragRender,
            isTree,
            rowDraggable,
            draggableId,
            dragRowHeight,
            virtual: true,
        }, cachedRows.length > 0, $statistics);
        return [cachedRows.concat(rows), $statistics];
    }, [tableStore, columnGroups, expandIconColumnIndex, lock, isTree, rowDraggable, rowDragRender, onClearCache, draggableId, dragRowHeight]);
    const renderGroup = useCallback((startIndex) => {
        const groups = [];
        const { rowMetaData } = statistics;
        if (rowMetaData) {
            const first = rowMetaData[startIndex];
            if (first && first.type === 'group' && first.groupLevel === 0) {
                return groups;
            }
            let level;
            rowMetaData.slice(0, startIndex).reverse().some((metaData) => {
                if (metaData.type === 'group') {
                    const { groupLevel = 0 } = metaData;
                    if (level === undefined || groupLevel < level) {
                        level = groupLevel;
                        groups.unshift(metaData.node);
                    }
                    return groupLevel === 0;
                }
                return false;
            });
        }
        return groups;
    }, [statistics]);
    const renderRow = useCallback(rIndex => totalRows[rIndex], [totalRows]);
    useEffect(action(() => {
        tableStore.lastMeasuredIndex = 0;
        const { rowMetaData } = statistics;
        tableStore.rowMetaData = rowMetaData;
        if (!rowMetaData) {
            const actualRows = totalRows.length;
            if (tableStore.actualRows !== actualRows) {
                tableStore.actualRows = actualRows;
            }
        }
    }), [totalRows, statistics, tableStore]);
    return totalRows.length ? (React.createElement(VirtualVerticalContainer, { renderBefore: tableStore.hasRowGroups ? renderGroup : undefined }, renderRow)) : getEmptyRow({ tableStore, lock, columnGroups });
};
VirtualRows.displayName = 'VirtualRows';
const Rows = function Rows(props) {
    const { lock, columnGroups, onClearCache, expandIconColumnIndex, tableStore, rowDragRender, isTree, rowDraggable } = props;
    const { cachedData, currentData, groupedData } = tableStore;
    const cachedRows = useComputed(() => (generateCachedRows({ tableStore, columnGroups, lock, isTree, rowDraggable, virtual: false }, onClearCache)), [cachedData, tableStore, columnGroups, onClearCache, lock, isTree, rowDraggable]);
    const hasCache = cachedRows.length > 0;
    const rows = useComputed(() => (generateRows({ tableStore, columnGroups, expandIconColumnIndex, lock, rowDragRender, isTree, rowDraggable, virtual: false }, hasCache)), [currentData, groupedData, tableStore, columnGroups, hasCache, expandIconColumnIndex, lock, isTree, rowDraggable, rowDragRender]);
    useEffect(action(() => {
        if (tableStore.actualRows !== undefined) {
            tableStore.actualRows = undefined;
        }
        if (tableStore.rowMetaData) {
            tableStore.rowMetaData = undefined;
        }
    }), [tableStore]);
    return cachedRows.length || rows.length ? (React.createElement(React.Fragment, null,
        cachedRows,
        rows)) : getEmptyRow({ tableStore, lock, columnGroups });
};
Rows.displayName = 'Rows';
const ObserverVirtualRows = observer(VirtualRows);
const ObserverRows = observer(Rows);
const TableTBody = function TableTBody(props) {
    const { lock, columnGroups, snapshot, dragRowHeight, ...rest } = props;
    const { prefixCls, tableStore, rowDragRender, dataSet, expandRowByClick, expandedRowRenderer, isTree } = useContext(TableContext);
    const { rowDraggable, propVirtual } = tableStore;
    const expandIconColumnIndex = !expandRowByClick && (expandedRowRenderer || isTree) ?
        (lock === ColumnLock.right ? columnGroups.leafs.filter(group => group.column.lock !== ColumnLock.right).length : 0) : -1;
    const handleResize = useCallback(action((_width, height) => {
        if (!tableStore.hidden) {
            if (tableStore.overflowY && height === tableStore.height) {
                height += 1;
            }
            tableStore.calcBodyHeight = height;
        }
    }), [tableStore]);
    const handleClearCache = useCallback(action(() => {
        dataSet.clearCachedRecords();
        tableStore.showCachedSelection = false;
    }), [dataSet, tableStore]);
    useLayoutEffect(() => {
        if (!lock) {
            const { node } = tableStore;
            if (node.isFocus &&
                !node.wrapper.contains(document.activeElement)) {
                node.focus();
            }
        }
    }, [lock, tableStore]);
    const renderClone = useRenderClone(lock ? undefined : columnGroups);
    const body = propVirtual ? (React.createElement(ObserverVirtualRows, { onClearCache: handleClearCache, expandIconColumnIndex: expandIconColumnIndex, columnGroups: columnGroups, tableStore: tableStore, rowDragRender: rowDragRender, lock: lock, isTree: isTree, rowDraggable: rowDraggable, snapshot: snapshot, dragRowHeight: dragRowHeight })) : (React.createElement(ObserverRows, { onClearCache: handleClearCache, expandIconColumnIndex: expandIconColumnIndex, columnGroups: columnGroups, tableStore: tableStore, rowDragRender: rowDragRender, lock: lock, isTree: isTree, rowDraggable: rowDraggable }));
    const tbody = rowDraggable && !propVirtual ? (React.createElement(Droppable, Object.assign({ droppableId: "table", key: "table", isCombineEnabled: isTree, mode: propVirtual ? 'virtual' : 'standard', renderClone: renderClone, getContainerForClone: () => instance(tableStore.node.getClassName(), prefixCls).tbody }, (rowDragRender && rowDragRender.droppableProps)), (droppableProvided) => (React.createElement("tbody", Object.assign({ ref: droppableProvided.innerRef }, droppableProvided.droppableProps, { className: `${prefixCls}-tbody` }, rest),
        body,
        droppableProvided.placeholder)))) : (React.createElement("tbody", Object.assign({ className: `${prefixCls}-tbody` }, rest), body));
    return lock ? tbody : (React.createElement(ReactResizeObserver, { onResize: handleResize, resizeProp: "height", immediately: true }, tbody));
};
TableTBody.displayName = 'TableTBody';
export default observer(TableTBody);
